import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/shared/Logo';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import QRCode from 'qrcode';
import QRCodeReact from 'react-qr-code';
import api from '../services/api';
import { copyToClipboard } from '../utils/clipboard';
import './ReceiveMoneyScreen.css';

interface UserInfo {
  name: string;
  walletId: string;
  phone: string;
}

export const ReceiveMoneyScreen: React.FC = () => {
  const navigate = useNavigate();
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState('');
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [isGeneratingQR, setIsGeneratingQR] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError('');

        // Get user profile information
        const userProfile = await api.users.getProfile();
        console.log('Profile response:', userProfile);

        // Get user's wallets to find the main wallet ID
        const wallets = await api.wallets.getWallets();
        console.log('Wallets response:', wallets);

        // Find the main wallet
        const mainWallet = wallets.find((wallet: any) => wallet.type === 'Main');
        
        if (!mainWallet) {
          throw new Error('No main wallet found');
        }

        // Set user info with real data
        const userData = {
          name: userProfile.name,
          walletId: mainWallet.walletId,
          phone: userProfile.phone
        };

        setUserInfo(userData);
        console.log('User data set:', userData);

      } catch (err: any) {
        console.error('Failed to fetch user data:', err);
        setError('Failed to load user information. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (!userInfo) return;

    // Generate QR code data
    const generateQR = async () => {
      console.log('Starting QR generation...');
      setIsGeneratingQR(true);
      
      // In a real app, this would be generated by the backend with encryption
      const qrPayload = {
        type: 'yebo_wallet_receive',
        walletId: userInfo.walletId,
        name: userInfo.name,
        phone: userInfo.phone,
        timestamp: Date.now(),
        // Add security token in real implementation
        token: 'secure_token_' + Math.random().toString(36).substr(2, 9)
      };
      
      const qrString = JSON.stringify(qrPayload);
      console.log('QR Data:', qrString);
      setQrData(qrString);

      // Try with simpler data first
      const simpleData = userInfo.walletId;
      console.log('Trying simple data:', simpleData);
      
      // Generate QR code as data URL
      try {
        // Try simple data first
        const qrDataUrl = await QRCode.toDataURL(simpleData, {
          width: 256,
          margin: 2,
          color: {
            dark: '#00A000',
            light: '#ffffff'
          },
          errorCorrectionLevel: 'M'
        });
        setQrImageUrl(qrDataUrl);
        console.log('QR code generated successfully as data URL with simple data');
      } catch (error) {
        console.error('Error generating QR code with simple data:', error);
        
        // Try with even simpler data
        try {
          const basicQr = await QRCode.toDataURL('Hello World', {
            width: 256,
            margin: 2
          });
          setQrImageUrl(basicQr);
          console.log('Basic QR code generated successfully');
        } catch (basicError) {
          console.error('Even basic QR generation failed:', basicError);
        }
      }
      
      setIsGeneratingQR(false);
    };

    // Simulate loading delay
    setTimeout(generateQR, 1500);
  }, [userInfo]);

  const copyWalletId = () => {
    if (!userInfo) return;
    copyToClipboard(userInfo.walletId, 'Wallet ID');
  };

  const shareQR = async () => {
    if (!userInfo) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Send me money on Yebo Wallet',
          text: `Send money to my Yebo Wallet: ${userInfo.walletId}`,
          url: window.location.href
        });
      } catch (err: any) {
        // Handle share cancellation or other errors silently
        // Users canceling share is normal behavior, not an error
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err.message);
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyWalletId();
    }
  };

  if (loading) {
    return (
      <div className="receive-money-screen">
        <div className="receive-money-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => navigate('/dashboard')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>Receive Money</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>
          <div className="screen-content">
            <div className="loading-state">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Loading your wallet information...</p>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  if (error) {
    return (
      <div className="receive-money-screen">
        <div className="receive-money-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => navigate('/dashboard')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>Receive Money</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>
          <div className="screen-content">
            <div className="error-state">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className="retry-button">
                Try Again
              </button>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  if (!userInfo) {
    return null;
  }

  return (
    <div className="receive-money-screen">
      <div className="receive-money-container">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Receive Money</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>

        <div className="screen-content">
          {/* User Info */}
          <div className="user-info-card">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-details">
              <h2>{userInfo.name}</h2>
              <p>Yebo Wallet ID: {userInfo.walletId}</p>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="qr-code-section">
            <h3>Scan to Send Money</h3>
            <div className="qr-code-container">
              {isGeneratingQR ? (
                <div className="qr-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                  <p>Generating QR Code...</p>
                </div>
              ) : (
                <div className="qr-code-display">
                  <div className="qr-code-canvas-container">
                    {qrImageUrl ? (
                      <img 
                        src={qrImageUrl} 
                        alt="QR Code for receiving money"
                        className="qr-image"
                        style={{ width: '256px', height: '256px' }}
                      />
                    ) : (
                      <div style={{ padding: '20px', backgroundColor: 'white' }}>
                        <QRCodeReact
                          value={qrData || userInfo.walletId}
                          size={216}
                          fgColor="#00A000"
                          bgColor="#ffffff"
                          level="M"
                        />
                      </div>
                    )}
                  </div>
                  <p className="qr-instruction">
                    Show this QR code to the person sending you money
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Alternative Options */}
          <div className="alternative-options">
            <div className="divider">
              <span>or share your details</span>
            </div>

            <div className="wallet-id-card">
              <div className="wallet-id-info">
                <label>Wallet ID</label>
                <span className="wallet-id">{userInfo.walletId}</span>
              </div>
              <button onClick={copyWalletId} className="copy-button">
                <i className="fas fa-copy"></i>
                Copy
              </button>
            </div>

            <div className="phone-card">
              <div className="phone-info">
                <label>Phone Number</label>
                <span className="phone-number">{userInfo.phone}</span>
              </div>
              <button onClick={() => copyToClipboard(userInfo?.phone || '', 'Phone number')} className="copy-button">
                <i className="fas fa-copy"></i>
                Copy
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button onClick={shareQR} className="share-button">
              <i className="fas fa-share-alt"></i>
              Share QR Code
            </button>
            <button onClick={() => navigate('/send-money')} className="send-money-button">
              <i className="fas fa-paper-plane"></i>
              Send Money Instead
            </button>
          </div>

          {/* Security Notice */}
          <div className="security-notice">
            <i className="fas fa-shield-alt"></i>
            <div>
              <h4>Safe & Secure</h4>
              <p>Your QR code is encrypted and expires after 10 minutes for your security.</p>
            </div>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};